import { useState } from 'react';

import { ActionIcon, Button, Group, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconPencil } from '@tabler/icons-react';

/**
 * The client's own reference (matter / PO number) — inline editable.
 * Clients identify their work by this everywhere codenames used to
 * appear (codenames are admin-only). Shared by the client request
 * detail and client report pages; both persist through the client
 * report-request PATCH.
 */
export function ClientReferenceRow({
  value,
  onSave,
}: Readonly<{
  value: string | null;
  onSave: (value: string | null) => Promise<void>;
}>) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await onSave(draft.trim() || null);
      setEditing(false);
    } catch (err) {
      notifications.show({
        title: 'Could not save your reference',
        message: err instanceof Error ? err.message : 'Please try again.',
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <Group gap={6} wrap="nowrap">
        <Text size="sm" c="dimmed" data-testid="client-reference">
          Your reference: {value ?? '—'}
        </Text>
        <ActionIcon
          size="xs"
          variant="subtle"
          color="gray"
          aria-label="Edit your reference"
          data-testid="edit-client-reference"
          onClick={() => {
            setDraft(value ?? '');
            setEditing(true);
          }}
        >
          <IconPencil size={12} />
        </ActionIcon>
      </Group>
    );
  }

  return (
    <Group gap={6} wrap="nowrap">
      <TextInput
        size="xs"
        value={draft}
        onChange={(e) => setDraft(e.currentTarget.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            void save();
          }
        }}
        placeholder="e.g. your matter or PO number"
        maxLength={255}
        w={240}
        data-testid="client-reference-input"
      />
      <Button size="compact-xs" onClick={() => void save()} loading={saving}>
        Save
      </Button>
      <Button
        size="compact-xs"
        variant="subtle"
        onClick={() => setEditing(false)}
        disabled={saving}
      >
        Cancel
      </Button>
    </Group>
  );
}
